import { useCallback, useEffect, useRef, useState } from 'react'
import { integrationsApi } from '@/api'
import type { IntegrationChannelItem } from '@/api/integrations/integrations.types'
import websocket from '@/utils/websocket'
import {
  CRM_REALTIME_CHAT_REFETCH_DEBOUNCE_MS,
  isIntegrationChannelDeletedWsMessage,
  useRefetchOnBrowserTabVisible,
} from '@pages/Admin/CRM/utils/crmRealtimeWebSocket'
import debounce from '@/utils/debounce/debounce'

export type UseWazzupCrmChannelsResult = {
  channels: IntegrationChannelItem[]
  isLoading: boolean
  error: string | null
  /** С индикатором загрузки (кнопка «Повторить»). */
  retry: () => void
  /** Без индикатора — WS / visibility / фоновые догоны. */
  silentRetry: () => void
}

type FetchOptions = {
  silent?: boolean
}

/** GET /integrations/channels/{integrationId} */
export const useWazzupCrmChannels = (
  integrationId: number | null,
  enabled = true,
  /** Увеличить, чтобы принудительно обновить список (назад к каналам, смена экрана). */
  refreshToken = 0,
): UseWazzupCrmChannelsResult => {
  const [channels, setChannels] = useState<IntegrationChannelItem[]>([])
  const [isLoading, setIsLoading] = useState(
    () => enabled && integrationId != null && integrationId > 0,
  )
  const [error, setError] = useState<string | null>(null)

  const requestIdRef = useRef(0)
  const integrationIdRef = useRef(integrationId)
  integrationIdRef.current = integrationId
  const enabledRef = useRef(enabled)
  enabledRef.current = enabled

  const fetchChannels = useCallback(async (options: FetchOptions = {}) => {
    const silent = Boolean(options.silent)
    const currentIntegrationId = integrationIdRef.current
    const currentEnabled = enabledRef.current

    if (
      !currentEnabled ||
      currentIntegrationId == null ||
      currentIntegrationId <= 0
    ) {
      setChannels([])
      setError(null)
      setIsLoading(false)
      return
    }

    const requestId = ++requestIdRef.current

    if (!silent) {
      setIsLoading(true)
      setError(null)
    }

    try {
      const res = await integrationsApi.getChannels(currentIntegrationId)
      if (requestId !== requestIdRef.current) {
        return
      }

      const raw = res?.data?.data ?? res?.data
      setChannels(Array.isArray(raw) ? raw : [])
      if (!silent) {
        setError(null)
      }
    } catch {
      if (requestId !== requestIdRef.current) {
        return
      }
      if (!silent) {
        setChannels([])
        setError('Не удалось загрузить каналы Wazzup')
      }
    } finally {
      // Снимаем лоадер у любого актуального запроса (в т.ч. silent):
      // иначе silent после non-silent оставляет isLoading=true навсегда.
      if (requestId === requestIdRef.current) {
        setIsLoading(false)
      }
    }
  }, [])

  const retry = useCallback(() => {
    void fetchChannels({ silent: false })
  }, [fetchChannels])

  const silentRetry = useCallback(() => {
    void fetchChannels({ silent: true })
  }, [fetchChannels])

  useEffect(() => {
    void fetchChannels({ silent: false })
  }, [enabled, fetchChannels, integrationId])

  const prevRefreshTokenRef = useRef(refreshToken)
  useEffect(() => {
    if (refreshToken === prevRefreshTokenRef.current) {
      return
    }
    prevRefreshTokenRef.current = refreshToken
    void fetchChannels({ silent: true })
  }, [fetchChannels, refreshToken])

  const silentRetryRef = useRef(silentRetry)
  silentRetryRef.current = silentRetry

  const debouncedSilentRetryRef = useRef(
    debounce(() => {
      silentRetryRef.current()
    }, CRM_REALTIME_CHAT_REFETCH_DEBOUNCE_MS),
  )

  const canRefetchOnVisible =
    enabled && integrationId != null && integrationId > 0

  useRefetchOnBrowserTabVisible(() => {
    debouncedSilentRetryRef.current()
  }, canRefetchOnVisible)

  useEffect(() => {
    if (!canRefetchOnVisible || integrationId == null) {
      return
    }

    websocket.connect()

    const onWs = (message: unknown) => {
      if (!isIntegrationChannelDeletedWsMessage(message)) {
        return
      }

      const deletedIntegrationId = Number(message.data.payload.integration_id)
      if (deletedIntegrationId !== integrationId) {
        return
      }

      debouncedSilentRetryRef.current()
    }

    const unsubscribe = websocket.subscribe(
      'integration_channel_deleted',
      onWs,
    )

    return () => {
      unsubscribe?.()
    }
  }, [canRefetchOnVisible, integrationId])

  return {
    channels,
    isLoading,
    error,
    retry,
    silentRetry,
  }
}
